import React, { ReactEventHandler, Dispatch } from 'react';
import { ArticleBreadcrumbsValue } from '../ArticleBreadcrumbs';
import { ArticleCoverPhotoValue } from '../ArticleCoverPhoto';
import { ArticleCostValue } from '../ArticleCost';
import { ArticleSectionContentListValue } from '../ArticleSectionContentList';
import { ArticleVetCountAnswerTextValue } from '../ArticleVetCountAnswerText';
import { HealthRatingValue } from '../HealthRating';
import { HealthArticleUGCVirtualListValue, HealthArticleUGCVirtualListProps } from '../HealthArticleUGCVirtualList';
import { RelatedArticlesValue } from '../RelatedArticles';
import { HealthArticleBannerValue } from '../HealthArticleBanner';
import { HealthArticleCommonDetailTextProps } from '../HealthArticleCommonDetailText';
import { HealthArticleSectionHealthExperienceListValue } from '../HealthArticleSectionHealthExperienceList';
import { JSONLD } from '../JSONLD';
import { HealthArticleContent } from './HealthArticleContent';
import { AnimalTypeSlug, Vote } from '../../constants/app';
import { usePostAuthToken } from '../../hooks/usePostAuthToken';
import { usePostRateEndpoint } from '../../hooks/usePostRateEndpoint';
import { UnpackRecord } from '../../types/unpackRecord';
import { Author, HealthArticleBody } from '../../types/app';
export type HealthArticleValue = {
  adviceSectionTitle: UnpackRecord<Pick<HealthArticleUGCVirtualListProps, 'title'>>;
  adviceList: HealthArticleUGCVirtualListValue;
  author: Author | null;
  articleBannerItems: HealthArticleBannerValue;
  articleId: number;
  articlePhotoSrc: ArticleCoverPhotoValue;
  articleVetCountLabel: ArticleVetCountAnswerTextValue;
  breadcrumbItems: ArticleBreadcrumbsValue;
  body: HealthArticleBody[];
  commonDetail: Pick<HealthArticleCommonDetailTextProps, 'label' | 'value'>;
  contentList: ArticleSectionContentListValue;
  healthExperienceList: HealthArticleSectionHealthExperienceListValue;
  healthRating: HealthRatingValue;
  ignoreDefinedCategories: boolean;
  isReviewed: boolean;
  title: string;
  relatedArticles: RelatedArticlesValue;

  // TODO: Type more specifically instead of "Object"
  structuredData: {
    // The package `schema-dts` causes us some memory heap issues
    // https://github.com/google/schema-dts/issues/34
    // We will now be removing it and using the `unknown` type
    // Documentation here https://blog.logrocket.com/when-to-use-never-and-unknown-in-typescript-5e4d6c5799ad/
    // TODO - We should this properly type this in the future something like `schema-dts`
    article: unknown;
    breadcrumbs: unknown;
    videos: unknown[] | null;
  };
  cost: ArticleCostValue;
  videoUrl: string;
};
// TODO - Partial for now. Will change on integration
type Props = Partial<Pick<HealthArticleUGCVirtualListProps['adviceProps'], 'onClickYes' | 'onClickNo'>> & {
  value: HealthArticleValue;
  articleType: 'Condition' | 'Symptom' | 'Treatment';
  metaTitle: string;
  slug: string;
  nextPageUrl: string;
  hasNextPageVetAdvice: boolean;
  isFetchingVetAdvice: boolean;
  animalTypeSlug?: AnimalTypeSlug;
  updateAdviceList?: Dispatch<React.SetStateAction<HealthArticleUGCVirtualListValue>>;
  onClickSeeMoreVetAdviceButton: ReactEventHandler<HTMLButtonElement>;
};
const voteAdvice = ({
  article,
  adviceListState,
  vote
}) => {
  const {
    id: updatedId,
    header: {
      numberOfVotes,
      ...restUpdatedHeader
    },
    vote: prevVote,
    ...restUpdated
  } = article;
  const hasVotedIncrement = vote === Vote.Yes ? 2 : -2;
  const notYetVotedIncrement = vote === Vote.Yes ? 1 : -1;
  const increment = prevVote ? hasVotedIncrement : notYetVotedIncrement;
  const updated = {
    ...restUpdated,
    header: {
      ...restUpdatedHeader,
      numberOfVotes: numberOfVotes + increment
    },
    vote
  };
  const updatedAdviceIndex = adviceListState.findIndex(({
    id
  }) => id === updatedId);
  const nextAdviceListState = [...adviceListState.slice(0, updatedAdviceIndex), updated, ...adviceListState.slice(updatedAdviceIndex + 1)];
  return nextAdviceListState;
};
export const HealthArticle = ({
  value: {
    adviceSectionTitle,
    adviceList,
    author,
    articleBannerItems,
    articleId,
    articlePhotoSrc,
    articleVetCountLabel,
    breadcrumbItems,
    commonDetail,
    body,
    contentList,
    healthExperienceList,
    healthRating,
    ignoreDefinedCategories,
    title,
    relatedArticles,
    cost,
    structuredData,
    videoUrl,
    isReviewed
  },
  articleType,
  metaTitle,
  slug,
  nextPageUrl,
  hasNextPageVetAdvice,
  isFetchingVetAdvice,
  animalTypeSlug,
  updateAdviceList,
  onClickSeeMoreVetAdviceButton
}: Props) => {
  const {
    execute: executeFetchToken
  } = usePostAuthToken({
    isAnonymous: true
  });
  const {
    execute: executeRateEndpoint
  } = usePostRateEndpoint();
  const handleOnClickUpVoteButton = async article => {
    if (article.vote === Vote.Yes) {
      return;
    }
    const result = await executeFetchToken();
    if (!result) {
      return;
    }
    await executeRateEndpoint({
      token: result,
      voteApiUrl: article.header.upvoteApiUrl
    });
    updateAdviceList(prevAdviceListState => voteAdvice({
      article,
      adviceListState: prevAdviceListState,
      vote: Vote.Yes
    }));
  };
  const handleOnClickDownVoteButton = async article => {
    if (article.vote === Vote.No) {
      return;
    }
    const result = await executeFetchToken();
    if (!result) {
      return;
    }
    await executeRateEndpoint({
      token: result,
      voteApiUrl: article.header.downvoteApiUrl
    });
    updateAdviceList(prevAdviceListState => voteAdvice({
      article,
      adviceListState: prevAdviceListState,
      vote: Vote.No
    }));
  };
  return <>
      <HealthArticleContent value={{
      adviceSectionTitle,
      adviceList,
      author,
      articleBannerItems,
      articleId,
      articlePhotoSrc,
      articleVetCountLabel,
      body,
      contentList,
      breadcrumbItems,
      commonDetail,
      healthExperienceList,
      healthRating,
      ignoreDefinedCategories,
      title,
      relatedArticles,
      structuredData,
      cost,
      videoUrl,
      isReviewed
    }} articleType={articleType} metaTitle={metaTitle} slug={slug} nextPageUrl={nextPageUrl} hasNextPageVetAdvice={hasNextPageVetAdvice} isFetchingVetAdvice={isFetchingVetAdvice} animalTypeSlug={animalTypeSlug} onClickYes={(_, article) => {
      handleOnClickUpVoteButton(article);
    }} onClickNo={(_, article) => {
      handleOnClickDownVoteButton(article);
    }} onClickSeeMoreVetAdviceButton={onClickSeeMoreVetAdviceButton} data-sentry-element="HealthArticleContent" data-sentry-source-file="index.tsx" />

      {/* Structured Data for SEO */}
      <JSONLD data-sentry-element="JSONLD" data-sentry-source-file="index.tsx">
        {structuredData.article}
      </JSONLD>

      <JSONLD data-sentry-element="JSONLD" data-sentry-source-file="index.tsx">
        {structuredData.breadcrumbs}
      </JSONLD>

      {structuredData.videos && structuredData.videos.map((video: {
      embedUrl: string;
    }) => <JSONLD key={video.embedUrl}>
            {video}
          </JSONLD>)}

      {adviceList.map((advice, index) => {
      /**
       * Since `adviceList` is a combined list
       * make sure to check for `structuredData` prop first
       * before rendering the structured data
       */
      if (!('structuredData' in advice)) {
        return null;
      }
      return <JSONLD key={`${advice.question.text}-${index}`}>
            {advice.structuredData}
          </JSONLD>;
    })}
    </>;
};
export type HealthArticleProps = Props;